/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/react-in-jsx-scope */
import { NotificationAlert, Table } from '../../../components';
import { columns } from './columns';
import { useEffect, useRef, useState } from 'react';
import { getAllUsers } from '../../../services/userServices';
import { AllReviewsProps, EmployeeData, MenteesProps } from '../../../types';
import { getAllQuarters } from '../../../services/reviewsServices';
import { AnnualReviewCycle } from '../../../types/AnnualReview';
import { getAllAnnualReviewCycles } from '../../../services/annualServices';
import dayjs from 'dayjs';
import AnnualReviewFeedbackStats from '../../../components/charts/AnnualReviewFeedbackStats';
import ActiveObjectivesChart from '../../../components/charts/ActiveObjectivesStats';
import { getMentorDashboardStats } from '../../../services/mentorDashboardStatsService';
import { MentorDashboardStats } from '../../../types/MentorDashboardStat';
import {
  InfoCircleFilled,
  CloseOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import CompetencyAnalysisChart from '../../../components/charts/CompetencyAnalysisStats';
import { DatePicker, DatePickerProps } from 'antd';

const AllMentees = () => {
  const [data, setData] = useState<EmployeeData[]>([]);
  const [loading, setLoading] = useState(true);
  const myRef = useRef<any>();
  const [dashboardStats, setDashboardStats] =
    useState<MentorDashboardStats | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [showAnnualReviewBanner, setShowAnnualReviewBanner] = useState(true);
  const [showQuarterlyReviewBanner, setShowQuarterlyReviewBanner] =
    useState(true);
  const [selectedYear, setSelectedYear] = useState<dayjs.Dayjs>(dayjs());

  const fetchUsers = async (): Promise<MenteesProps[] | undefined> => {
    const { data, error } = await getAllUsers('mentor');
    if (error) {
      myRef.current?.call('error', error?.message);
      return undefined;
    }
    return data;
  };

  const fetchReviews = async (): Promise<AllReviewsProps[] | undefined> => {
    const { data, error } = await getAllQuarters();
    if (error) {
      myRef.current?.call('error', error?.message);
      return undefined;
    }
    return data;
  };

  const fetchAnnualCycles = async (): Promise<
    AnnualReviewCycle[] | undefined
  > => {
    const { data, error } = await getAllAnnualReviewCycles('mentor');
    if (error) {
      myRef.current?.call('error', error?.message);
      return undefined;
    }
    return data;
  };

  const fetchQuarterCycles = async (): Promise<
    AllReviewsProps[] | undefined
  > => {
    const { data, error } = await getAllQuarters('mentor');
    if (error) {
      myRef.current?.call('error', error?.message);
      return undefined;
    }
    return data;
  };

  const fetchDashboardStats = async (year: number) => {
    const { data, error } = await getMentorDashboardStats(year);
    if (error) {
      myRef.current?.call('error', error?.message);
      return;
    }
    setDashboardStats(data || null);
  };

  const bindEmployeeAndReviewData = (
    empData: MenteesProps[],
    reviewData: AllReviewsProps[],
    annualCycleData: AnnualReviewCycle[],
    quarterCycleData: AllReviewsProps[]
  ) => {
    const data: EmployeeData[] = empData.map((employee) => {
      return {
        ...employee,
        cycleId:
          reviewData.find(
            (review) =>
              dayjs(dayjs().toDate()).isAfter(review.startDate) &&
              dayjs(dayjs().toDate()).isBefore(review.endDate)
          )?.id || '',
        cycleName:
          reviewData.find(
            (review) =>
              dayjs(dayjs().toDate()).isAfter(review.startDate) &&
              dayjs(dayjs().toDate()).isBefore(review.endDate)
          )?.name || '',
        annualData: annualCycleData.filter((cycle) => cycle.status >= 1),
        quarterData: quarterCycleData.filter((cycle) => cycle.status >= 1),
      };
    });
    setData(data);
  };

  const fetchData = async (year: number) => {
    setLoading(true);
    const empData = await fetchUsers();
    const reviewData = await fetchReviews();
    const annualCycleData = await fetchAnnualCycles();
    const quarterCycleData = await fetchQuarterCycles();
    await fetchDashboardStats(year);

    bindEmployeeAndReviewData(
      empData as MenteesProps[],
      reviewData as AllReviewsProps[],
      annualCycleData as AnnualReviewCycle[],
      quarterCycleData as AllReviewsProps[]
    );
    setLoading(false);
  };

  useEffect(() => {
    fetchData(selectedYear?.year());

    AnnualReviewDeadlineBanner();
    QuarterlyDeadlineBanner();
  }, [selectedYear]);

  const menteesWithoutObjectives =
    dashboardStats?.menteesWithoutObjectives.mentees;

  // const quarterlyDeadlineMissedMentees =
  //   dashboardStats?.quarterlyFeedbackDeadlinesMissedCount.mentees;

  const openMenteesModal = () => {
    setIsModalOpen(true);
  };

  // const openMenteesFeebackModal = () => {
  //   setIsFeedbackModalOpen(true);
  // };

  const closeMenteesModal = () => {
    setIsModalOpen(false);
  };

  // const closeFeedbackModal = () => {
  //   setIsFeedbackModalOpen(false);
  // };

  const calculateDaysRemaining = (
    mentorDeadline: number | dayjs.Dayjs | Date | null | undefined
  ) => {
    if (!mentorDeadline) return 0;
    const deadline = dayjs(mentorDeadline);
    const today = dayjs();
    const daysRemainingw = deadline.diff(today, 'day');
    return daysRemainingw;
  };

  // Annual review deadline banner component
  const AnnualReviewDeadlineBanner = () => {
    // Don't render if still loading or no dashboard stats
    if (loading || !dashboardStats) {
      return null;
    }

    const annualDeadline =
      dashboardStats?.menteeAnnualReviewStats.mentorDeadline;
    const daysRemaining = calculateDaysRemaining(annualDeadline);

    // if annualDeadline is null, then return null
    if (!annualDeadline) {
      return null;
    }

    // Don't render if more than 14 days remaining
    if (daysRemaining > 14) {
      return null;
    }

    const isOverdue = daysRemaining < 0;

    if (daysRemaining > 14) {
      return null;
    }

    return (
      <div className="flex flex-col gap-2">
        {isOverdue ? (
          <div className="flex items-center gap-2 w-full p-3 bg-[#FFF1F0] border border-[#FFA39E] rounded-lg">
            <div className="flex-1 flex items-center gap-2">
              <svg
                width="18"
                height="20"
                viewBox="0 0 18 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.8571 6.42864V5.5715C11.8571 5.49293 11.7929 5.42864 11.7143 5.42864H4.85714C4.77857 5.42864 4.71429 5.49293 4.71429 5.5715V6.42864C4.71429 6.50721 4.77857 6.5715 4.85714 6.5715H11.7143C11.7929 6.5715 11.8571 6.50721 11.8571 6.42864ZM4.85714 8.00007C4.77857 8.00007 4.71429 8.06436 4.71429 8.14293V9.00007C4.71429 9.07864 4.77857 9.14293 4.85714 9.14293H8.14286C8.22143 9.14293 8.28571 9.07864 8.28571 9.00007V8.14293C8.28571 8.06436 8.22143 8.00007 8.14286 8.00007H4.85714ZM11.5714 10.0715C9.44107 10.0715 7.71429 11.7983 7.71429 13.9286C7.71429 16.059 9.44107 17.7858 11.5714 17.7858C13.7018 17.7858 15.4286 16.059 15.4286 13.9286C15.4286 11.7983 13.7018 10.0715 11.5714 10.0715ZM13.4911 15.8483C12.9786 16.3608 12.2964 16.6429 11.5714 16.6429C10.8464 16.6429 10.1643 16.3608 9.65179 15.8483C9.13929 15.3358 8.85714 14.6536 8.85714 13.9286C8.85714 13.2036 9.13929 12.5215 9.65179 12.009C10.1643 11.4965 10.8464 11.2144 11.5714 11.2144C12.2964 11.2144 12.9786 11.4965 13.4911 12.009C14.0036 12.5215 14.2857 13.2036 14.2857 13.9286C14.2857 14.6536 14.0036 15.3358 13.4911 15.8483ZM11 15.3572C11 15.5088 11.0602 15.6541 11.1674 15.7613C11.2745 15.8684 11.4199 15.9286 11.5714 15.9286C11.723 15.9286 11.8683 15.8684 11.9755 15.7613C12.0827 15.6541 12.1429 15.5088 12.1429 15.3572C12.1429 15.2057 12.0827 15.0603 11.9755 14.9532C11.8683 14.846 11.723 14.7858 11.5714 14.7858C11.4199 14.7858 11.2745 14.846 11.1674 14.9532C11.0602 15.0603 11 15.2057 11 15.3572ZM11.2143 14.2144H11.9286C12.0071 14.2144 12.0714 14.1501 12.0714 14.0715V12.0715C12.0714 11.9929 12.0071 11.9286 11.9286 11.9286H11.2143C11.1357 11.9286 11.0714 11.9929 11.0714 12.0715V14.0715C11.0714 14.1501 11.1357 14.2144 11.2143 14.2144ZM7.42857 16.0715H3.28571V3.50007H13.2857V9.64293C13.2857 9.7215 13.35 9.78578 13.4286 9.78578H14.4286C14.5071 9.78578 14.5714 9.7215 14.5714 9.64293V2.78578C14.5714 2.46971 14.3161 2.21436 14 2.21436H2.57143C2.25536 2.21436 2 2.46971 2 2.78578V16.7858C2 17.1019 2.25536 17.3572 2.57143 17.3572H7.42857C7.50714 17.3572 7.57143 17.2929 7.57143 17.2144V16.2144C7.57143 16.1358 7.50714 16.0715 7.42857 16.0715Z"
                  fill="#CF1322"
                />
              </svg>
              <span className="text-[#bb3235]">
                Your remaining mentee annual reviews are{' '}
                <span className="font-bold">
                  overdue by {Math.abs(daysRemaining)}{' '}
                  {Math.abs(daysRemaining) === 1 ? 'day' : 'days'}
                </span>
                . Please complete them now.
              </span>
            </div>
            <button
              onClick={() => setShowAnnualReviewBanner(false)}
              className="hover:bg-[#FFF1F0]/80 p-1 rounded"
            >
              <CloseOutlined className="text-[#FF4D4F]" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between w-full p-3 bg-[#FFFBE6] border border-[#FFE58F] rounded-lg">
            <div className="flex items-center gap-2">
              <InfoCircleFilled className="text-[#FAAD14]" />
              <span>
                Reminder!{' '}
                <span className="font-bold">
                  {Math.abs(daysRemaining)}{' '}
                  {Math.abs(daysRemaining) === 1 ? 'day ' : 'days '}
                </span>
                {isOverdue ? 'overdue' : 'left'} to submit all mentee annual
                reviews.
              </span>
            </div>
            <button
              onClick={() => setShowAnnualReviewBanner(false)}
              className="hover:bg-gray-100 p-1 rounded"
            >
              <CloseOutlined className="text-gray-500" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // Quarterly Deadline banner component
  const QuarterlyDeadlineBanner = () => {
    // Don't render if still loading or no dashboard stats
    if (loading || !dashboardStats) {
      return null;
    }

    const quarterlyDeadline =
      dashboardStats?.quarterlyFeedbackDeadlinesMissedCount
        .mentorDeadlineQuarterly;

    // if quarterlyDeadline is null, then return null
    if (!quarterlyDeadline) {
      return null;
    }

    // // mock for testing
    // const quarterlyDeadline = dayjs().subtract(3, 'day').toDate();

    const daysRemaining = calculateDaysRemaining(quarterlyDeadline);
    const isOverdue = daysRemaining < 0;

    // Don't render if more than 14 days remaining
    if (daysRemaining > 14) {
      return null;
    }

    return (
      <div className="flex flex-col gap-2">
        {/* Overdue Warning */}
        {isOverdue ? (
          <div className="flex items-center gap-2 w-full p-3 bg-[#FFF1F0] border border-[#FFA39E] rounded-lg">
            <div className="flex-1 flex items-center gap-2">
              <svg
                width="18"
                height="20"
                viewBox="0 0 18 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.8571 6.42864V5.5715C11.8571 5.49293 11.7929 5.42864 11.7143 5.42864H4.85714C4.77857 5.42864 4.71429 5.49293 4.71429 5.5715V6.42864C4.71429 6.50721 4.77857 6.5715 4.85714 6.5715H11.7143C11.7929 6.5715 11.8571 6.50721 11.8571 6.42864ZM4.85714 8.00007C4.77857 8.00007 4.71429 8.06436 4.71429 8.14293V9.00007C4.71429 9.07864 4.77857 9.14293 4.85714 9.14293H8.14286C8.22143 9.14293 8.28571 9.07864 8.28571 9.00007V8.14293C8.28571 8.06436 8.22143 8.00007 8.14286 8.00007H4.85714ZM11.5714 10.0715C9.44107 10.0715 7.71429 11.7983 7.71429 13.9286C7.71429 16.059 9.44107 17.7858 11.5714 17.7858C13.7018 17.7858 15.4286 16.059 15.4286 13.9286C15.4286 11.7983 13.7018 10.0715 11.5714 10.0715ZM13.4911 15.8483C12.9786 16.3608 12.2964 16.6429 11.5714 16.6429C10.8464 16.6429 10.1643 16.3608 9.65179 15.8483C9.13929 15.3358 8.85714 14.6536 8.85714 13.9286C8.85714 13.2036 9.13929 12.5215 9.65179 12.009C10.1643 11.4965 10.8464 11.2144 11.5714 11.2144C12.2964 11.2144 12.9786 11.4965 13.4911 12.009C14.0036 12.5215 14.2857 13.2036 14.2857 13.9286C14.2857 14.6536 14.0036 15.3358 13.4911 15.8483ZM11 15.3572C11 15.5088 11.0602 15.6541 11.1674 15.7613C11.2745 15.8684 11.4199 15.9286 11.5714 15.9286C11.723 15.9286 11.8683 15.8684 11.9755 15.7613C12.0827 15.6541 12.1429 15.5088 12.1429 15.3572C12.1429 15.2057 12.0827 15.0603 11.9755 14.9532C11.8683 14.846 11.723 14.7858 11.5714 14.7858C11.4199 14.7858 11.2745 14.846 11.1674 14.9532C11.0602 15.0603 11 15.2057 11 15.3572ZM11.2143 14.2144H11.9286C12.0071 14.2144 12.0714 14.1501 12.0714 14.0715V12.0715C12.0714 11.9929 12.0071 11.9286 11.9286 11.9286H11.2143C11.1357 11.9286 11.0714 11.9929 11.0714 12.0715V14.0715C11.0714 14.1501 11.1357 14.2144 11.2143 14.2144ZM7.42857 16.0715H3.28571V3.50007H13.2857V9.64293C13.2857 9.7215 13.35 9.78578 13.4286 9.78578H14.4286C14.5071 9.78578 14.5714 9.7215 14.5714 9.64293V2.78578C14.5714 2.46971 14.3161 2.21436 14 2.21436H2.57143C2.25536 2.21436 2 2.46971 2 2.78578V16.7858C2 17.1019 2.25536 17.3572 2.57143 17.3572H7.42857C7.50714 17.3572 7.57143 17.2929 7.57143 17.2144V16.2144C7.57143 16.1358 7.50714 16.0715 7.42857 16.0715Z"
                  fill="#CF1322"
                />
              </svg>
              <span className="text-[#CF1322]">
                Your remaining mentee quarterly reviews are{' '}
                <span className="font-bold ">
                  overdue by {Math.abs(daysRemaining)}{' '}
                  {Math.abs(daysRemaining) === 1 ? 'day' : 'days'}
                </span>
                . Please complete them now.
              </span>
            </div>
            <button
              onClick={() => setShowQuarterlyReviewBanner(false)}
              className="hover:bg-[#FFF1F0]/80 p-1 rounded"
            >
              <CloseOutlined className="text-[#FF4D4F]" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between w-full p-3 bg-[#FFFBE6] border border-[#FFE58F] rounded-lg">
            <div className="flex items-center gap-2">
              <InfoCircleFilled className="text-[#FAAD14]" />
              <span>
                Reminder!{' '}
                <span className="font-bold">
                  {Math.abs(daysRemaining)}{' '}
                  {Math.abs(daysRemaining) === 1 ? 'day ' : 'days '}
                </span>
                {isOverdue ? 'overdue' : 'left'} to submit all mentee quarterly
                reviews.
              </span>
            </div>
            <button
              onClick={() => setShowQuarterlyReviewBanner(false)}
              className="hover:bg-gray-100 p-1 rounded"
            >
              <CloseOutlined className="text-gray-500" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // date change function
  const dateChange: DatePickerProps['onChange'] = (date) => {
    if (date) {
      setSelectedYear(date);
    } else {
      setSelectedYear(dayjs());
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-y-[10px]">
        <div className="p-[10px] flex flex-col">
          <div className="flex flex-col items-end">
            <DatePicker
              onChange={dateChange}
              picker="year"
              className="ml-3 w-fit mb-4"
              placeholder="Year"
              value={selectedYear}
            />
          </div>
          <hr />
          <div className="text-[20px] font-[500]">Mentee Dashboard</div>
          <hr />

          <div className="grid grid-cols-2 gap-4">
            <div>
              {showAnnualReviewBanner && <AnnualReviewDeadlineBanner />}
            </div>
            <div>
              {showQuarterlyReviewBanner && <QuarterlyDeadlineBanner />}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4 shadow-sm mt-6">
              <h3 className="text-md font-semibold mb-4 text-left">
                Annual Review Feedback Status
              </h3>
              <AnnualReviewFeedbackStats
                pendingReviewCount={
                  dashboardStats?.menteeAnnualReviewStats.pendingReviews
                }
                completedReviewCount={
                  dashboardStats?.menteeAnnualReviewStats.completedReviews
                }
                totalReviews={
                  dashboardStats?.menteeAnnualReviewStats.totalReviews
                }
                pendingReviewList={
                  dashboardStats?.menteeAnnualReviewStats.pendingMenteesList
                }
                completedReviewList={
                  dashboardStats?.menteeAnnualReviewStats.completedMenteesList
                }
              />
            </div>

            {/* quarterly deadlines missed and 0 objectives */}
            <div className="grid grid-cols-1 gap-4 h-[225px]">
              {/* <div className="grid grid-cols-[2fr_1fr] gap-4"> */}
              {/* <div className="border border-customBlueBorder rounded-lg p-4 shadow-sm mt-6 bg-customBlue h-[150px]">
                  <h3 className="text-md font-semibold mb-4 text-center">
                    Total quarterly feedback deadlines missed
                  </h3>
                  <div className="flex justify-between">
                    <div className="text-center flex-1">
                      <p className="text-2xl font-bold">
                        {
                          dashboardStats?.quarterlyFeedbackDeadlinesMissedCount
                            .mentorCount
                        }
                      </p>
                      <p className="text-sm">By Me</p>
                    </div>
                    <div className="text-center flex-1">
                      <p className="text-2xl font-bold">
                        {
                          dashboardStats?.quarterlyFeedbackDeadlinesMissedCount
                            .menteeCount
                        }
                      </p>
                      <p className="text-sm">By Mentees</p>
                    </div>
                  </div>
                  <div className="flex justify-between mt-2">
                    <div className="text-right flex-1">
                      <span
                        onClick={openMenteesFeebackModal}
                        className="text-blue-600 cursor-pointer hover:underline"
                      >
                        <MoreOutlined style={{ color: 'black' }} />
                      </span>
                    </div>
                  </div>
                </div> */}

              {/* quarterly deadline missed modal */}
              {/* {isFeedbackModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white p-6 rounded-lg max-w-md w-full relative">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-xl font-bold">
                        Quarterly feedback deadlines missed
                      </h2>
                      <button
                        onClick={closeFeedbackModal}
                        className="absolute top-6 right-6 text-gray-500 hover:text-gray-700 text-2xl"
                      >
                        ×
                      </button>
                    </div>
                    <hr className="border-t border-gray-300 mb-4" />
                    <h2 className="text-md font-bold mb-2">Mentees</h2>
                    {quarterlyDeadlineMissedMentees && (
                      <ul className="space-y-2 list-disc ml-8">
                        {quarterlyDeadlineMissedMentees.map((mentee, index) => (
                          <li key={index}>{mentee}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )} */}

              <div className="border border-customOrangeBorder rounded-lg p-4 shadow-sm mt-6 bg-customOrange h-[150px] items-center">
                <h3 className="text-xl font-semibold mb-4 text-center">
                  Mentees with 0 objectives set
                </h3>
                <div className="text-center flex-1">
                  <p className="text-4xl font-bold">
                    {dashboardStats?.menteesWithoutObjectives.totalMentees}
                  </p>
                </div>
                <div className="text-right">
                  <span
                    onClick={openMenteesModal}
                    className="text-blue-600 cursor-pointer hover:underline"
                  >
                    <MoreOutlined style={{ color: 'black' }} />
                  </span>
                </div>

                {/* o objectives modal */}
                {isModalOpen && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg max-w-md w-full relative">
                      <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-bold">
                          Mentees With 0 Objectives
                        </h2>
                        <button
                          onClick={closeMenteesModal}
                          className="absolute top-6 right-6 text-gray-500 hover:text-gray-700 text-2xl"
                        >
                          ×
                        </button>
                      </div>
                      <hr className="border-t border-gray-300 mb-4" />
                      <h2 className="text-md font-bold mb-2">Mentees</h2>
                      {menteesWithoutObjectives && (
                        <ul className="space-y-2 list-disc ml-8">
                          {menteesWithoutObjectives.map((mentee, index) => (
                            <li key={index}>{mentee}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* </div> */}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4 shadow-sm mt-6">
              <h3 className="text-md font-semibold mb-4 text-left">
                Average competency Analysis
              </h3>
              <div className="opacity-45">
                Average ratings given by mentors for each mentee
              </div>
              <CompetencyAnalysisChart
                competencyExpectedScore={
                  dashboardStats?.competencyAnalysis.competencyExpectScores
                }
                menteePerformance={
                  dashboardStats?.competencyAnalysis
                    .menteeCompetencyPerformances
                }
                competencyLevelPerformance={
                  dashboardStats?.competencyAnalysis.competencyLevelPerformance
                }
                competencies={dashboardStats?.competencyAnalysis.competencies}
                averageCompetencies={
                  dashboardStats?.competencyAnalysis.averageCompetencies
                }
              />
            </div>

            <div className="border border-gray-300 rounded-lg p-4 mb-72 shadow-sm -mt-64">
              <h3 className="text-md font-semibold mb-4 text-left">
                Status of Mentee&apos;s Objectives
              </h3>
              <ActiveObjectivesChart
                completedObjectives={
                  dashboardStats?.mentorActiveObjectiveStatus
                    .completedObjectives
                }
                inProgressObjectives={
                  dashboardStats?.mentorActiveObjectiveStatus
                    .inProgressObjectives
                }
                notStartedObjectives={
                  dashboardStats?.mentorActiveObjectiveStatus
                    .notStartedObjectives
                }
                draftObjectives={
                  dashboardStats?.mentorActiveObjectiveStatus.draftObjectives
                }
                totalObjectives={
                  dashboardStats?.mentorActiveObjectiveStatus.totalObjectives
                }
                menteeObjectiveStats={
                  dashboardStats?.mentorActiveObjectiveStatus
                    .menteeObjectiveStats
                }
                objectiveCount={
                  dashboardStats?.mentorActiveObjectiveStatus.objectiveCount
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-y-[10px]">
        <div className="p-[10px] flex flex-col">
          <div className="text-[20px] font-[500]">Assigned Mentees</div>
          <div className="opacity-45">
            Effortlessly oversee your team&apos;s performance! Ability to view
            an assigned mentee&apos;s profile, and access the mentee&apos;s
            objective setting page for streamlined performance management and
            strategic talent development.
          </div>
        </div>

        <Table
          columns={columns}
          data={data}
          errorFetchingData={false}
          loading={loading}
          rowKey="id"
        />
        <NotificationAlert ref={myRef} />
      </div>
    </div>
  );
};

export default AllMentees;
